"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { MemberStatusBadge } from "@/app/[tenant]/(console)/settings/memberships/helpers";
import { ProgressBarLink } from "@/components/progress-bar";
import { useUser } from "@/contexts/user-provider";
import { useClinicDay } from "@/hooks/use-clinic-day";
import { formatCurrency, formatDate, formatFrequency, formatMembershipName } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { MemberStatus } from "@/types/enums/membership";
import type { MemberTiny } from "@/types/inventory/member";
import { BenefitUsageList } from "./benefit-usage-list";
import { PatientSidebarSectionSkeleton } from "./section-skeleton";
import { memberQueryOptions, useMemberIssuedDiscounts } from "@/query/inventory/member";
interface Props {
  patientId: string;
  memberships: MemberTiny[];
  isLoading?: boolean;
}
export default function MembershipsSection(t0) {
  const $ = _c(10);
  const {
    patientId,
    memberships,
    isLoading
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <PatientSidebarSectionSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  let t2;
  if ($[1] !== memberships || $[2] !== patientId) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const visibleMemberships = (memberships ?? []).filter(_temp);
      if (visibleMemberships.length === 0) {
        let t3;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <p className="text-caption text-muted-foreground">No memberships</p>;
          $[5] = t3;
        } else {
          t3 = $[5];
        }
        t2 = t3;
        break bb0;
      }
      let t3;
      if ($[6] !== patientId) {
        t3 = member_0 => <ProgressBarLink key={member_0.id} href={`/patient/${patientId}?tab=memberships`} className="mb-3 block rounded-md p-2 transition-colors hover:bg-hover"><div className="flex items-center justify-between"><span className="text-body-strong">{formatMembershipName(member_0.membership)}</span><MemberStatusBadge status={member_0.status} /></div><MemberStatusLine member={member_0} /><MemberBenefitUsage memberId={member_0.id} /></ProgressBarLink>;
        $[6] = patientId;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      t1 = visibleMemberships.map(t3);
    }
    $[1] = memberships;
    $[2] = patientId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[8] !== t1) {
    t3 = <>{t1}</>;
    $[8] = t1;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

/**
 * One line on where the membership stands. Only a delinquent one fetches
 * anything: what it owes, from the past-due endpoint.
 */
function _temp(member) {
  return member.status !== MemberStatus.INACTIVE;
}
function MemberStatusLine(t0) {
  const $ = _c(31);
  const {
    member
  } = t0;
  const clinicDay = useClinicDay();
  const isDelinquent = member.status === MemberStatus.DELINQUENT;
  let t1;
  if ($[0] !== member.id) {
    t1 = memberQueryOptions.pastDue(member.id);
    $[0] = member.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isDelinquent || $[3] !== t1) {
    t2 = {
      ...t1,
      enabled: isDelinquent
    };
    $[2] = isDelinquent;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: pastDue
  } = useQuery(t2);
  if (isDelinquent) {
    let t3;
    if ($[5] !== pastDue) {
      t3 = pastDue ? `${formatCurrency(pastDue.amountDue)} past due` : "Payment past due";
      $[5] = pastDue;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t3) {
      t4 = <div className="mt-1 text-caption text-status-error-text">{t3}</div>;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (member.status === MemberStatus.FROZEN) {
    let t3;
    if ($[9] !== member.unfreezeDate) {
      t3 = member.unfreezeDate ? `Frozen until ${formatDate(member.unfreezeDate, {
        calendarDay: true
      })}` : "Frozen";
      $[9] = member.unfreezeDate;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== t3) {
      t4 = <div className="mt-1 text-caption text-status-info-text">{t3}</div>;
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  if (member.status === MemberStatus.ACTIVE && member.freezeDate && clinicDay(member.freezeDate) >= clinicDay(new Date())) {
    let t3;
    if ($[13] !== member.freezeDate) {
      t3 = formatDate(member.freezeDate, {
        calendarDay: true
      });
      $[13] = member.freezeDate;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] !== t3) {
      t4 = <div className="mt-1 text-caption text-status-info-text">Freezes {t3}</div>;
      $[15] = t3;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    return t4;
  }
  const note = member.status === MemberStatus.AWAITING_FORM ? "Waiting on the enrollment form" : member.status === MemberStatus.PENDING ? "Waiting for the payment to clear" : null;
  const price = member.customPrice ?? member.membership.price;
  let t3;
  if ($[17] !== member.billingFrequency || $[18] !== price) {
    t3 = price != null ? `${formatCurrency(price)}${member.billingFrequency ? `/${formatFrequency(member.billingFrequency)}` : " per cycle"}` : null;
    $[17] = member.billingFrequency;
    $[18] = price;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== clinicDay || $[21] !== member.nextBillingDate || $[22] !== member.startDate || $[23] !== member.status || $[24] !== note) {
    t4 = note ?? (member.status === MemberStatus.ACTIVE && member.nextBillingDate ? `next charge ${formatDate(clinicDay(member.nextBillingDate))}` : `Member since ${formatDate(clinicDay(member.startDate))}`);
    $[20] = clinicDay;
    $[21] = member.nextBillingDate;
    $[22] = member.startDate;
    $[23] = member.status;
    $[24] = note;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== t3 || $[27] !== t4) {
    t5 = [t3, t4].filter(Boolean);
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  const t6 = t5.join(" \xB7 ");
  let t7;
  if ($[29] !== t6) {
    t7 = <div className="mt-1 text-caption text-foreground-secondary">{t6}</div>;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  return t7;
}
function MemberBenefitUsage(t0) {
  const $ = _c(8);
  const {
    memberId
  } = t0;
  const {
    currentUser
  } = useUser();
  let t1;
  if ($[0] !== currentUser) {
    t1 = hasPermission(currentUser, PERMISSIONS.BILLING_READ);
    $[0] = currentUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canReadBilling = t1;
  let t2;
  if ($[2] !== canReadBilling) {
    t2 = {
      enabled: canReadBilling
    };
    $[2] = canReadBilling;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: t3
  } = useMemberIssuedDiscounts(memberId, t2);
  let t4;
  if ($[4] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const benefits = t4;
  let t5;
  if ($[6] !== benefits) {
    t5 = <BenefitUsageList benefits={benefits} />;
    $[6] = benefits;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}